import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import './AulasDocentes.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import aulaDocenteService, {
  aulaDocenteArchivosService,
  aulaDocenteLinksService,
  aulaDocenteMensajesService
} from '../services/AulaDocenteService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import AulaRecursosModal from './common/AulaRecursosModal';
import AulaDocenteStatsModal from './common/AulaDocenteStatsModal';
import ProfessorMenu from './common/menus/ProfessorMenu';
import AdminMenu from './common/menus/AdminMenu';
import SearchableSelect from './common/SearchableSelect';
import { hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';

const DEFAULT_ITEMS_PER_PAGE = 10;

const getRange = (page, pageSize) => {
  const take = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = Math.max(page - 1, 0) * take;
  return { start, end: start + take - 1 };
};

const getId = (item) => item?.id || item?.Id || '';

const getAulaNombre = (aula) => aula?.nombre || aula?.Nombre || 'Aula docente';

const getFilterItemId = (item) => String(item?.id || item?.Id || '').trim();

const getFilterItemName = (item, fallback = 'Sin nombre') => (
  item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || fallback
);

const getFilterItemEmail = (item) => item?.email || item?.Email || '';

const getFilterItemTotalAulas = (item) => Number(item?.totalAulas ?? item?.TotalAulas ?? 0);

const getTopProfessorName = (item) => item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || 'Profesor';

const getTopProfessorEmail = (item) => item?.email || item?.Email || '';

const getTopProfessorTotalAulas = (item) => Number(item?.totalAulas ?? item?.TotalAulas ?? 0);

const getAulaAssistantCount = (aula) => {
  const explicit = aula?.cantidadAsistentes ?? aula?.CantidadAsistentes;
  if (explicit !== undefined && explicit !== null) return Number(explicit) || 0;
  const ids = aula?.idsAsistentes || aula?.IdsAsistentes || [];
  return Array.isArray(ids) ? ids.length : 0;
};

const getSafeAccordionKey = (value) => String(value || 'aula').replace(/[^a-zA-Z0-9_-]/g, '-');

const getUserId = (user) => String(user?.id || user?.Id || user?.userId || user?.UserId || user?.idUsuario || user?.IdUsuario || '').trim();

const getDocentesText = (aula) => {
  const direct = aula?.nombresDocentes || aula?.NombresDocentes;
  if (direct) return direct;
  const docentes = aula?.docentes || aula?.Docentes || [];
  if (!Array.isArray(docentes) || docentes.length === 0) return '—';
  return docentes.map((d) => d?.nombre || d?.Nombre || d?.email || d?.Email).filter(Boolean).join(', ') || '—';
};

const getAulaMateriasDetalle = (aula) => {
  const materias = aula?.materiasDetalle || aula?.MateriasDetalle || [];
  return Array.isArray(materias) ? materias : [];
};

const getMateriaDetalleNombre = (item) => item?.nombreMateria || item?.NombreMateria || item?.nombre || item?.Nombre || 'Materia';

const getMateriaDetalleCurso = (item) => {
  const curso = item?.curso || item?.Curso;
  if (curso) return curso;
  const cursos = item?.cursos || item?.Cursos || [];
  return Array.isArray(cursos) && cursos.length > 0 ? cursos.join(', ') : '';
};

const getMateriaDetalleDivision = (item) => item?.division || item?.Division || '';

const getAulaActivo = (aula) => (
  typeof aula?.activo === 'boolean' ? aula.activo : typeof aula?.Activo === 'boolean' ? aula.Activo : true
);

const canManageAula = (aula) => Boolean(aula?.puedeEditar ?? aula?.PuedeEditar ?? false);

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AulasDocentes = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [aulas, setAulas] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [materiaIds, setMateriaIds] = useState([]);
  const [profesorIds, setProfesorIds] = useState([]);
  const [materiasOptions, setMateriasOptions] = useState([]);
  const [profesoresOptions, setProfesoresOptions] = useState([]);
  const [profesoresCatalog, setProfesoresCatalog] = useState([]);
  const [topProfesores, setTopProfesores] = useState([]);
  const [toggleLoadingId, setToggleLoadingId] = useState('');
  const [resourcesModal, setResourcesModal] = useState({ open: false, aula: null });
  const [statsModal, setStatsModal] = useState({ open: false, aula: null });
  const [openSections, setOpenSections] = useState({ managed: true, shared: true });
  const [expandedAulas, setExpandedAulas] = useState({});

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const roleName = normalizeRoleName(user);
  const isAdminUser = roleName.includes('admin');
  const isGeneralAdmin = isAdminUser && !isAdminColegio(user);
  const currentUserId = getUserId(user).toLowerCase();
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT');
  }, [featureFlags]);
  const hasMensajesAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteEnviarMensajesEnAula');
  }, [featureFlags]);
  const hasArchivosAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteSubirArchivosAlAula');
  }, [featureFlags]);
  const hasPizarraAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermitePizarraCompartidaEnAula');
  }, [featureFlags]);
  const hasChatAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteChatGrupalEnAula');
  }, [featureFlags]);
  const hasVideollamadaAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteIniciarVideoLlamadaEnAula');
  }, [featureFlags]);
  const hasLinksAula = true;
  const hasRecursos = hasMensajesAula || hasArchivosAula || hasAgentesClasicos || hasOpenAIChats || hasLinksAula || hasPizarraAula || hasChatAula || hasVideollamadaAula;

  const loadProfesoresCatalog = useCallback(async () => {
    if (!activeColegioId || !isAdminUser) {
      setProfesoresCatalog([]);
      return;
    }

    try {
      const profesores = await aulaDocenteService.getProfesoresParaAulaDocente(activeColegioId);
      setProfesoresCatalog(Array.isArray(profesores) ? profesores : []);
    } catch (err) {
      console.warn('No pudimos cargar el catálogo completo de docentes:', err);
      setProfesoresCatalog([]);
    }
  }, [activeColegioId, isAdminUser]);

  const loadAulas = useCallback(async () => {
    if (!activeColegioId) return;

    setIsLoading(true);
    setError('');
    try {
      const { start, end } = getRange(page, pageSize);
      const response = await aulaDocenteService.getAulasDocentes({
        idColegio: activeColegioId,
        start,
        end,
        search: search.trim(),
        materiaIds,
        profesorIds,
        includeTotal: true
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      setAulas(items);
      setTotal(response?.total ?? items.length);
      setMateriasOptions(Array.isArray(response?.materias) ? response.materias : []);
      setProfesoresOptions(Array.isArray(response?.profesores) ? response.profesores : []);
      setTopProfesores(Array.isArray(response?.topProfesores) ? response.topProfesores : []);
    } catch (err) {
      setError(err.message || 'No pudimos cargar las aulas docentes.');
      setAulas([]);
      setTotal(0);
      setTopProfesores([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeColegioId, page, pageSize, search, materiaIds, profesorIds]);

  const handleAulaUpdated = useCallback((updatedAula) => {
    const updatedId = updatedAula?.id || updatedAula?.Id;
    if (!updatedId) return;
    setResourcesModal((prev) => (
      prev.aula && (prev.aula.id || prev.aula.Id) === updatedId
        ? { ...prev, aula: { ...prev.aula, ...updatedAula } }
        : prev
    ));
    setAulas((prev) =>
      prev.map((item) => ((item.id || item.Id) === updatedId ? { ...item, ...updatedAula } : item))
    );
  }, []);

  useEffect(() => {
    loadAulas();
  }, [loadAulas]);

  useEffect(() => {
    loadProfesoresCatalog();
  }, [loadProfesoresCatalog]);

  useEffect(() => {
    setPage(1);
  }, [search, materiaIds, profesorIds]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, pageSize, total]);

  const handleToggle = useCallback(async (aula) => {
    const aulaId = getId(aula);
    const activo = typeof aula?.activo === 'boolean' ? aula.activo : typeof aula?.Activo === 'boolean' ? aula.Activo : true;
    if (!aulaId) return;

    setToggleLoadingId(aulaId);
    try {
      await aulaDocenteService.updateAulaDocenteActivo(aulaId, !activo);
      setAulas((prev) => prev.map((item) => (
        getId(item) === aulaId ? { ...item, activo: !activo, Activo: !activo } : item
      )));
    } catch (err) {
      window.alert(err.message || 'No pudimos actualizar el estado del aula docente.');
    } finally {
      setToggleLoadingId('');
    }
  }, []);

  const materiaSelectOptions = useMemo(() => (
    materiasOptions
      .map((item) => {
        const id = getFilterItemId(item);
        if (!id) return null;
        const totalAulas = getFilterItemTotalAulas(item);
        const codigo = item?.codigo || item?.Codigo || '';
        return {
          value: id,
          label: getFilterItemName(item, 'Materia'),
          secondary: `${totalAulas} aula${totalAulas === 1 ? '' : 's'}${codigo ? ` · ${codigo}` : ''}`,
          searchText: codigo
        };
      })
      .filter(Boolean)
  ), [materiasOptions]);

  const profesoresConTotales = useMemo(() => {
    const totalsById = new Map(
      profesoresOptions
        .map((item) => [getFilterItemId(item), getFilterItemTotalAulas(item)])
        .filter(([id]) => Boolean(id))
    );

    const source = profesoresCatalog.length > 0 ? profesoresCatalog : profesoresOptions;
    const byId = new Map();

    source.forEach((item) => {
      const id = getFilterItemId(item);
      if (!id) return;
      byId.set(id, {
        ...item,
        totalAulas: totalsById.get(id) ?? getFilterItemTotalAulas(item)
      });
    });

    profesoresOptions.forEach((item) => {
      const id = getFilterItemId(item);
      if (!id || byId.has(id)) return;
      byId.set(id, item);
    });

    return Array.from(byId.values())
      .sort((a, b) => getFilterItemName(a, 'Profesor').localeCompare(getFilterItemName(b, 'Profesor'), 'es'));
  }, [profesoresCatalog, profesoresOptions]);

  const profesorSelectOptions = useMemo(() => (
    profesoresConTotales
      .map((item) => {
        const id = getFilterItemId(item);
        if (!id) return null;
        const totalAulas = getFilterItemTotalAulas(item);
        const email = getFilterItemEmail(item);
        return {
          value: id,
          label: getFilterItemName(item, 'Profesor'),
          secondary: `${totalAulas} aula${totalAulas === 1 ? '' : 's'}${email ? ` · ${email}` : ''}`,
          searchText: email
        };
      })
      .filter(Boolean)
  ), [profesoresConTotales]);

  const profesoresSinAulas = useMemo(() => (
    profesoresConTotales
      .filter((item) => getFilterItemTotalAulas(item) === 0)
      .sort((a, b) => getFilterItemName(a, 'Profesor').localeCompare(getFilterItemName(b, 'Profesor'), 'es'))
  ), [profesoresConTotales]);

  const hasFilters = search.trim() || materiaIds.length > 0 || profesorIds.length > 0;

  const managedAulas = useMemo(() => aulas.filter(canManageAula), [aulas]);
  const sharedAulas = useMemo(() => aulas.filter((aula) => !canManageAula(aula)), [aulas]);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));

  const toggleSection = useCallback((sectionKey) => {
    setOpenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const toggleAulaDetails = useCallback((aulaKey) => {
    setExpandedAulas((prev) => ({ ...prev, [aulaKey]: !prev[aulaKey] }));
  }, []);

  const renderAulaCard = (aula, mode) => {
    const aulaId = getId(aula);
    const aulaKey = `${mode}-${aulaId || getAulaNombre(aula)}`;
    const safeAulaKey = getSafeAccordionKey(aulaKey);
    const detailId = `aula-docente-detail-${safeAulaKey}`;
    const isExpanded = Boolean(expandedAulas[aulaKey]);
    const activo = getAulaActivo(aula);
    const isOwner = currentUserId && String(aula.idCreador || aula.IdCreador || '').toLowerCase() === currentUserId;
    const descripcion = aula.descripcion || aula.Descripcion || '';
    const codigo = aula.nicknameUrl || aula.NicknameUrl || aula.codigoSuscripcion || aula.CodigoSuscripcion || '—';
    const creador = aula.nombreCreador || aula.NombreCreador || '—';
    const ciclo = aula.cicloLectivo || aula.CicloLectivo || '—';
    const fechaAlta = aula.fechaAlta || aula.FechaAlta;
    const cantidadAsistentes = getAulaAssistantCount(aula);
    const materiasDetalle = getAulaMateriasDetalle(aula);
    const visibleMateriasDetalle = materiasDetalle.slice(0, 3);
    const hiddenMateriasCount = Math.max(materiasDetalle.length - visibleMateriasDetalle.length, 0);

    return (
      <article key={aulaKey} className={`aula-docente-card ${mode === 'managed' ? 'managed' : 'shared'} ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <button
          type="button"
          className="aula-docente-card-summary"
          aria-expanded={isExpanded}
          aria-controls={detailId}
          onClick={() => toggleAulaDetails(aulaKey)}
        >
          <span className="aula-docente-card-summary-main">
            <span className="aula-docente-card-title-row">
              <span className="aula-docente-card-title">{getAulaNombre(aula)}</span>
              <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                {activo ? 'Activa' : 'Inactiva'}
              </span>
            </span>
            <span className="aula-docente-summary-meta">
              <i className="fas fa-user" aria-hidden="true" />
              {creador}
            </span>
            <span className="aula-docente-academic-list">
              {visibleMateriasDetalle.length > 0 ? (
                <>
                  {visibleMateriasDetalle.map((materia) => {
                    const materiaNombre = getMateriaDetalleNombre(materia);
                    const curso = getMateriaDetalleCurso(materia);
                    const division = getMateriaDetalleDivision(materia);
                    return (
                      <span key={`${materia.idMateria || materia.IdMateria || materiaNombre}-${curso}-${division}`} className="aula-docente-academic-chip">
                        <span className="aula-docente-academic-main">
                          <i className="fas fa-book" aria-hidden="true" />
                          {materiaNombre}
                        </span>
                        <span className="aula-docente-academic-detail">
                          <i className="fas fa-layer-group" aria-hidden="true" />
                          Curso: {curso || 'Sin curso'}
                        </span>
                        <span className="aula-docente-academic-detail">
                          <i className="fas fa-code-branch" aria-hidden="true" />
                          División: {division || 'Sin división'}
                        </span>
                      </span>
                    );
                  })}
                  {hiddenMateriasCount > 0 && (
                    <span className="aula-docente-academic-more">+{hiddenMateriasCount} más</span>
                  )}
                </>
              ) : (
                <span className="aula-docente-academic-empty">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  Sin materia, curso o división asociados
                </span>
              )}
            </span>
          </span>
          <span className="aula-docente-card-summary-right">
            <span className={`aula-docente-owner-chip ${mode === 'managed' ? 'owned' : 'shared'}`}>
              <i className={`fas ${mode === 'managed' ? 'fa-user-shield' : 'fa-user-plus'}`} />
              {mode === 'managed' ? (isOwner ? 'Propietario' : 'Administrable') : 'Compartida'}
            </span>
            <i className={`fas fa-chevron-down aula-docente-card-chevron ${isExpanded ? 'open' : ''}`} aria-hidden="true" />
          </span>
        </button>

        {isExpanded && (
          <div id={detailId} className="aula-docente-card-body">
            {descripcion && <p className="aula-docente-description">{descripcion}</p>}

            <dl className="aula-docente-meta-grid">
              <div>
                <dt>Código</dt>
                <dd>{codigo}</dd>
              </div>
              <div>
                <dt>Creador</dt>
                <dd>{creador}</dd>
              </div>
              <div>
                <dt>Ciclo</dt>
                <dd>{ciclo}</dd>
              </div>
              <div>
                <dt>Asistentes</dt>
                <dd>{cantidadAsistentes}</dd>
              </div>
              <div>
                <dt>Creada</dt>
                <dd>{formatDate(fechaAlta)}</dd>
              </div>
            </dl>

            <div className="aula-docente-docentes">
              <span>Docentes asociados</span>
              <p>{getDocentesText(aula)}</p>
            </div>

            <div className="aula-docente-card-actions">
              {mode === 'managed' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/aulas-docentes/${aulaId}/ingresar`)}
                  >
                    <i className="fas fa-sign-in-alt" />
                    Ingresar
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/aulas-docentes/${aulaId}/editar`)}
                  >
                    <i className="fas fa-edit" />
                    Editar
                  </button>
                  {hasRecursos && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setResourcesModal({ open: true, aula })}
                    >
                      <i className="fas fa-layer-group" />
                      Recursos
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStatsModal({ open: true, aula })}
                  >
                    <i className="fas fa-chart-line" />
                    Estadísticas
                  </button>
                  <button
                    type="button"
                    className={`btn ${activo ? 'btn-danger-soft' : 'btn-success-soft'}`}
                    disabled={toggleLoadingId === aulaId}
                    onClick={() => handleToggle(aula)}
                  >
                    <i className={`fas ${toggleLoadingId === aulaId ? 'fa-spinner fa-spin' : activo ? 'fa-ban' : 'fa-check-circle'}`} />
                    {activo ? 'Deshabilitar' : 'Habilitar'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/aulas-docentes/${aulaId}/ingresar`)}
                >
                  <i className="fas fa-sign-in-alt" />
                  Ingresar
                </button>
              )}
            </div>
          </div>
        )}
      </article>
    );
  };

  const renderAulasSection = ({ sectionKey, className = '', title, description, icon, items, mode, emptyIcon, emptyText }) => {
    const isOpen = openSections[sectionKey];
    const bodyId = `aulas-docentes-section-${sectionKey}`;

    return (
      <section className={`aulas-docentes-section ${className} ${isOpen ? 'open' : 'closed'}`}>
        <button
          type="button"
          className="aulas-docentes-section-header"
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={() => toggleSection(sectionKey)}
        >
          <span className="aulas-docentes-section-title">
            <i className={`fas ${icon}`} aria-hidden="true" />
            <span className="aulas-docentes-section-copy">
              <span className="aulas-docentes-section-heading">{title}</span>
              <span className="aulas-docentes-section-description">{description}</span>
            </span>
          </span>
          <span className="aulas-docentes-section-actions">
            <span className="aulas-docentes-section-count">{items.length}</span>
            <i className={`fas fa-chevron-down aulas-docentes-section-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true" />
          </span>
        </button>
        {isOpen && (
          <div id={bodyId} className="aulas-docentes-section-body">
            {items.length > 0 ? (
              <div className="aulas-docentes-grid">
                {items.map((aula) => renderAulaCard(aula, mode))}
              </div>
            ) : (
              <div className="aulas-docentes-empty-inline">
                <i className={`fas ${emptyIcon}`} />
                <span>{emptyText}</span>
              </div>
            )}
          </div>
        )}
      </section>
    );
  };

  const handleProfessorNavigate = (item) => {
    if (!item?.path) return;
    setIsSidebarOpen(false);
    navigate(item.path);
  };

  const handleAdminNavigate = (item) => {
    setIsSidebarOpen(false);
    if (!item) return;
    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
      return;
    }
    if (item.id === 'aulas-docentes') {
      navigate('/aulas-docentes');
      return;
    }
    if (item.id === 'agentes-clasicos-gpt') {
      navigate('/mis-agentes-clasicos-gpt');
      return;
    }
    navigate('/admin-dashboard', { state: { tab: item.id } });
  };

  const handleBackToSelectColegio = useCallback(() => {
    ColegioService.clearColegioData();
    navigate('/admin/select-colegio', { replace: true });
  }, [navigate]);

  return (
    <div className={`new-dashboard my-assistants ${isAdminUser ? 'admin-mode' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className={`fas ${isAdminUser ? 'fa-university' : 'fa-graduation-cap'}`}></i>
            <span>{isAdminUser ? 'Admin' : 'aluxIA'}</span>
          </div>
        </div>

        {isAdminUser ? (
          <AdminMenu
            activeId="aulas-docentes"
            user={user}
            featureFlags={featureFlags}
            onNavigate={handleAdminNavigate}
          />
        ) : (
          <ProfessorMenu
            activeId="aulas-docentes"
            hasOpenAIChats={hasOpenAIChats}
            hasAgentesClasicos={hasAgentesClasicos}
            hasModuloAlumnos={isFeatureEnabled(featureFlags, 'ModuloAlumnos')}
            onNavigate={handleProfessorNavigate}
          />
        )}

        <div className="sidebar-footer">
          {isAdminUser && <ActiveColegioBadge />}
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className={`fas ${isAdminUser ? 'fa-user-shield' : 'fa-user'}`}></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.roleName || user?.role || (isAdminColegio(user) ? 'Admin colegio' : 'Profesor')}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-chalkboard-teacher"></i>
              Aulas docentes
            </h1>
            <p className="page-subtitle">Aulas docentes del colegio {activeColegioName}</p>
          </div>

          <div className="header-right">
            {!isAdminUser && <SolicitudesDropdown />}
            <ActiveColegioBadge />
            <UserSessionMenu
              user={user}
              onLogout={logout}
              extraItems={isGeneralAdmin ? [{ key: 'switch-school', label: 'Cambiar colegio', icon: 'fas fa-exchange-alt', onClick: handleBackToSelectColegio }] : []}
              userFallbackLabel={isAdminUser ? 'Administrador' : 'Profesor'}
            />
          </div>
        </header>

        <div className="dashboard-content">
          {isAdminUser && (
            <div className="aula-list-insights">
              <div className="aula-list-total-card">
                <span className="aula-list-total-icon">
                  <i className="fas fa-chalkboard-teacher" />
                </span>
                <div>
                  <p>Aulas encontradas</p>
                  <strong>{total}</strong>
                </div>
              </div>

              <div className="aula-list-top-card">
                <div className="aula-list-top-header">
                  <span>
                    <i className="fas fa-chalkboard-teacher" />
                    Top 10 profesores creadores
                  </span>
                </div>
                {topProfesores.length > 0 ? (
                  <div className="aula-list-top-table" role="table" aria-label="Top 10 profesores creadores de aulas docentes">
                    <div className="aula-list-top-row header" role="row">
                      <span>Profesor</span>
                      <span>Aulas</span>
                    </div>
                    <div className="aula-list-top-body">
                      {topProfesores.map((profesorTop) => (
                        <div
                          className="aula-list-top-row"
                          role="row"
                          key={profesorTop.idProfesor || profesorTop.IdProfesor || getTopProfessorName(profesorTop)}
                        >
                          <span className="aula-list-top-info">
                            <span className="aula-list-top-name">{getTopProfessorName(profesorTop)}</span>
                            {getTopProfessorEmail(profesorTop) && (
                              <span className="aula-list-top-email">{getTopProfessorEmail(profesorTop)}</span>
                            )}
                          </span>
                          <strong>{getTopProfessorTotalAulas(profesorTop)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="aula-list-top-empty">Sin profesores con aulas.</p>
                )}
              </div>

              <div className="aula-list-empty-card">
                <div className="aula-list-top-header">
                  <span>
                    <i className="fas fa-user-slash" />
                    Profesores sin aulas docentes
                  </span>
                  <strong className="aula-list-empty-count">{profesoresSinAulas.length}</strong>
                </div>
                {profesoresSinAulas.length > 0 ? (
                  <div className="aula-list-empty-list">
                    {profesoresSinAulas.map((profesorSinAula) => (
                      <div
                        className="aula-list-empty-row"
                        key={getFilterItemId(profesorSinAula) || getFilterItemName(profesorSinAula, 'Profesor')}
                      >
                        <span className="aula-list-top-info">
                          <span className="aula-list-top-name">{getFilterItemName(profesorSinAula, 'Profesor')}</span>
                          {getFilterItemEmail(profesorSinAula) && (
                            <span className="aula-list-top-email">{getFilterItemEmail(profesorSinAula)}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="aula-list-top-empty">Todos los profesores crearon aulas docentes.</p>
                )}
              </div>
            </div>
          )}

          <div className="content-card aula-list-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-chalkboard-teacher"></i>
                <span>Listado de aulas docentes</span>
              </div>
              <div className="professor-actions">
                <button className="btn btn-primary" type="button" onClick={() => navigate('/aulas-docentes/nuevo')}>
                  <i className="fas fa-plus"></i>
                  Crear aula docente
                </button>
              </div>
            </div>
            <div className="aula-list-filter-bar">
              <div className="aula-list-filter-field aula-list-filter-search">
                <label>Texto</label>
                <div className="professor-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Buscar por aula, código o creador"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="aula-list-filter-field">
                <label>Materias</label>
                <SearchableSelect
                  multiple
                  values={materiaIds}
                  options={materiaSelectOptions}
                  placeholder="Todas las materias"
                  emptyText="No hay materias disponibles."
                  onChange={(nextValues) => {
                    setMateriaIds(nextValues);
                    setPage(1);
                  }}
                />
              </div>
              <div className="aula-list-filter-field">
                <label>Profesor creador</label>
                <SearchableSelect
                  multiple
                  values={profesorIds}
                  options={profesorSelectOptions}
                  placeholder="Todos los creadores"
                  emptyText="No hay profesores disponibles."
                  onChange={(nextValues) => {
                    setProfesorIds(nextValues);
                    setPage(1);
                  }}
                />
              </div>
              {hasFilters && (
                <button
                  className="aula-list-clear-filters"
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setMateriaIds([]);
                    setProfesorIds([]);
                    setPage(1);
                  }}
                >
                  <i className="fas fa-times" />
                  Limpiar
                </button>
              )}
            </div>

            <div className="card-content aulas-docentes-content">
              {!isLoading && error && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <p>{error}</p>
                </div>
              )}
              {!error && isLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                  </div>
                  <p>Cargando aulas docentes...</p>
                </div>
              )}

              {!error && !isLoading && aulas.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-chalkboard-teacher"></i>
                  </div>
                  <p>No hay aulas docentes asignadas.</p>
                </div>
              )}

              {!error && !isLoading && aulas.length > 0 && (
                <>
                  {isAdminUser ? (
                    renderAulasSection({
                      sectionKey: 'managed',
                      title: 'Aulas docentes creadas por profesores',
                      description: 'Listado por docente creador, con acceso de administración, recursos y estadísticas.',
                      icon: 'fa-chalkboard-teacher',
                      items: aulas,
                      mode: 'managed',
                      emptyIcon: 'fa-chalkboard-teacher',
                      emptyText: 'No hay aulas docentes creadas por profesores en esta página.'
                    })
                  ) : (
                    <>
                      {renderAulasSection({
                        sectionKey: 'managed',
                        title: 'Mis aulas docentes',
                        description: 'Aulas donde podés administrar configuración, recursos y estadísticas.',
                        icon: 'fa-user-shield',
                        items: managedAulas,
                        mode: 'managed',
                        emptyIcon: 'fa-user-shield',
                        emptyText: 'No tenés aulas docentes propias en esta página.'
                      })}

                      {renderAulasSection({
                        sectionKey: 'shared',
                        className: 'shared',
                        title: 'Aulas docentes compartidas',
                        description: 'Aulas de otros docentes donde podés ingresar como participante.',
                        icon: 'fa-user-plus',
                        items: sharedAulas,
                        mode: 'shared',
                        emptyIcon: 'fa-user-plus',
                        emptyText: 'No hay aulas docentes compartidas en esta página.'
                      })}
                    </>
                  )}

                  <div className="aulas-docentes-pagination">
                    <div className="aulas-docentes-page-size">
                      <span>Mostrar</span>
                      <select
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setPage(1);
                        }}
                      >
                        {[6, 10, 20, 50].map((size) => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div className="aulas-docentes-page-controls">
                      <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
                        <i className="fas fa-chevron-left" />
                        Anterior
                      </button>
                      <span>Página {page} de {totalPages}</span>
                      <button type="button" className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
                        Siguiente
                        <i className="fas fa-chevron-right" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {resourcesModal.open && (
        <AulaRecursosModal
          open={resourcesModal.open}
          aula={resourcesModal.aula}
          hasMensajes={hasMensajesAula}
          hasArchivos={hasArchivosAula}
          hasAsistentes={hasAgentesClasicos}
          hasLinks
          globalAvailability={{
            mostrarArchivos: hasArchivosAula,
            mostrarLinks: hasLinksAula,
            mostrarPizarra: hasPizarraAula,
            mostrarChat: hasChatAula,
            mostrarVideollamada: hasVideollamadaAula,
            mostrarForo: hasMensajesAula,
            mostrarAsistentesIA: hasAgentesClasicos
          }}
          resourceVisibilityService={(id, payload) => aulaDocenteService.updateAulaDocenteRecursosVisibilidad(id, payload)}
          onAulaUpdated={handleAulaUpdated}
          canUploadFiles
          labels={{ resource: 'aula docente', resourceTitle: 'aula docente' }}
          mensajesService={aulaDocenteMensajesService}
          archivosService={aulaDocenteArchivosService}
          linksService={aulaDocenteLinksService}
          asistentesService={aulaDocenteService}
          showAlumnoFilter={false}
          onClose={() => setResourcesModal({ open: false, aula: null })}
        />
      )}

      {statsModal.open && (
        <AulaDocenteStatsModal
          open={statsModal.open}
          aula={statsModal.aula}
          onClose={() => setStatsModal({ open: false, aula: null })}
        />
      )}
    </div>
  );
};

export default AulasDocentes;
